import { Link, useParams } from '@tanstack/react-router';
import { SystemStatusBadge } from '@tempo-io/sui-badge';
import { TableCell, TableRow } from '@tempo-io/sui-table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useTranslation } from 'react-i18next';
import { WorkIntegration_Keys } from '@/lib/api/types/work-integrations';
import type { IntegrationSyncFailedItem } from '@/lib/api/work-integrations/syncFailures';
import { SyncFailureType } from '@/lib/api/work-integrations/syncFailures';
import { useGetAllWorkIntegrationsById } from '@/lib/queries/work-integrations/useGetAllWorkIntegrations';
import { useInstanceFieldMappingsQuery } from '@/lib/queries/work-integrations/useInstanceFieldMappingsQuery';
import {
  COL_AFFECTED,
  COL_DIRECTION,
  COL_ITEM,
  COL_LOOP,
  COL_MESSAGE,
  COL_TYPE,
} from './syncFailureColumns';

export type SyncFailureRowVariant = 'integration' | 'item';

type Props = {
  failure: IntegrationSyncFailedItem;
  variant?: SyncFailureRowVariant;
};

const isFieldValueMappingFailure = (fields: Record<string, string>): boolean =>
  Object.keys(fields).length > 0 && Object.keys(fields).every((k) => /^\d+$/.test(k));

export function SyncFailureRow({ failure, variant = 'integration' }: Props) {
  const { t } = useTranslation();
  const { org } = useParams({ strict: false }) as { org?: string };
  const { data: integrationsById } = useGetAllWorkIntegrationsById({
    enabled: variant === 'integration',
  });
  const integration = integrationsById?.[failure.integrationId];
  const loopId = integration?.loopId;
  const instanceId = integration?.instanceId;
  const showLoopColumn = variant === 'integration';
  const showItemColumn = variant === 'integration';

  const jiraLink =
    failure.sourceItemKey &&
    integration?.sourceSystemType === WorkIntegration_Keys.JIRA &&
    integration.instanceUrl
      ? `${integration.instanceUrl}/browse/${failure.sourceItemKey}`
      : null;

  const { data: fieldMappings = [] } = useInstanceFieldMappingsQuery({ cloudId: instanceId });
  const fieldNameById = new Map(fieldMappings.map((m) => [String(m.id), m.tempoFieldName]));

  const itemLabel =
    failure.sourceItemKey ??
    (failure.itemId !== null
      ? t(($) => $.common.settings['work-integrations'].detail.syncFailures.unknownItem, {
          itemId: failure.itemId,
        })
      : t(($) => $.common.settings['work-integrations'].detail.syncFailures.untitledItem));

  const directionLabel =
    failure.syncDirection === 'PULL'
      ? t(($) => $.common.settings['work-integrations'].detail.syncFailures.direction.pull)
      : t(($) => $.common.settings['work-integrations'].detail.syncFailures.direction.push);

  const typeVariant: 'error' | 'warning' | 'default' =
    failure.type === SyncFailureType.PERMANENT
      ? 'error'
      : failure.type === SyncFailureType.TRANSIENT
        ? 'warning'
        : 'default';

  const typeLabel =
    failure.type === SyncFailureType.PERMANENT
      ? t(($) => $.common.settings['work-integrations'].detail.syncFailures.type.permanent)
      : failure.type === SyncFailureType.TRANSIENT
        ? t(($) => $.common.settings['work-integrations'].detail.syncFailures.type.transient)
        : null;

  const failedFields = failure.failedFields ?? {};
  const fieldValueMapping = isFieldValueMappingFailure(failedFields);

  return (
    <TableRow data-testid="sync-failure-row">
      {showLoopColumn && (
        <TableCell className={`${COL_LOOP} align-top font-mono text-xs`}>{loopId ?? '—'}</TableCell>
      )}
      {showItemColumn && (
        <TableCell className={`${COL_ITEM} align-top font-mono text-xs`}>
          {jiraLink ? (
            <a
              href={jiraLink}
              target="_blank"
              rel="noopener noreferrer"
              title={t(($) => $.common.settings['work-integrations'].viewInJira)}
              className="block truncate text-text-link hover:underline"
            >
              {itemLabel}
            </a>
          ) : (
            <span className="block truncate">{itemLabel}</span>
          )}
        </TableCell>
      )}
      <TableCell className={`${COL_DIRECTION} align-top`}>
        <SystemStatusBadge variant="default">{directionLabel}</SystemStatusBadge>
      </TableCell>
      <TableCell className={`${COL_TYPE} align-top`}>
        {typeLabel && <SystemStatusBadge variant={typeVariant}>{typeLabel}</SystemStatusBadge>}
      </TableCell>
      <TableCell
        className={`${COL_MESSAGE} align-top text-sm text-text-neutral-primary break-words`}
      >
        {failure.message}
      </TableCell>
      <TableCell className={`${COL_AFFECTED} align-top text-xs text-text-neutral-secondary`}>
        {fieldValueMapping ? (
          <ul className="space-y-1">
            {Object.keys(failedFields).map((mappingId) => {
              const name = fieldNameById.get(mappingId) ?? mappingId;
              return (
                <li key={mappingId}>
                  {variant === 'integration' && instanceId && org ? (
                    <Link
                      to="/org/$org/settings/work-integrations"
                      params={{ org }}
                      search={{ instanceId, openValueMapping: Number(mappingId) }}
                      target="_blank"
                      className="text-text-link hover:underline"
                    >
                      {name}
                    </Link>
                  ) : (
                    <span>{name}</span>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          Object.keys(failedFields).length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default">{Object.keys(failedFields).join(', ')}</span>
              </TooltipTrigger>
              <TooltipContent>
                <ul className="space-y-1">
                  {Object.keys(failedFields).map((field) => (
                    <li key={field}>
                      <span className="font-medium">{field}:</span> {failedFields[field]}
                    </li>
                  ))}
                </ul>
              </TooltipContent>
            </Tooltip>
          )
        )}
      </TableCell>
    </TableRow>
  );
}
